import {forwardRef, useCallback, useId, useRef, useSyncExternalStore, type ComponentPropsWithoutRef, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import {FieldLabel, FieldMessage} from '../lib/field';
import {useTextareaHeight, VALUE_EVENTS} from '../lib/useTextareaHeight';
import type {Testable} from '../lib/testable';
import {Stack} from '../Stack';
import {Typography} from '../Typography';

export interface TextAreaProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'className'>, Testable {
	label?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	className?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
	label,
	error,
	hint,
	required = false,
	disabled = false,
	maxLength,
	id,
	testId,
	className,
	...props
}, ref) => {
	const generatedId = useId();
	const innerRef = useRef<HTMLTextAreaElement>(null);
	const textAreaId = id ?? generatedId;
	const messageId = `${textAreaId}-message`;
	const message = error ?? hint;
	const border = disabled ? 'border-background-500'
		: error ? 'border-tone-red bg-background-700'
			: 'border-foreground-400 hover:border-foreground-300 focus-visible:border-foreground-300';

	useTextareaHeight(innerRef);

	const subscribeToValue = useCallback((onStoreChange: () => void) => {
		const el = innerRef.current;
		VALUE_EVENTS.forEach(event => el?.addEventListener(event, onStoreChange));
		return () => VALUE_EVENTS.forEach(event => el?.removeEventListener(event, onStoreChange));
	}, []);

	// The element is the store. register() writes it through the ref, firing no event and changing no
	// prop, so props cannot see a defaultValues prefill
	const count = useSyncExternalStore(
		subscribeToValue,
		() => innerRef.current?.value.length ?? 0,
		() => 0
	);

	const setRefs = (el: HTMLTextAreaElement | null) => {
		innerRef.current = el;
		if (typeof ref === 'function') ref(el);
		else if (ref) (ref as {current: HTMLTextAreaElement | null}).current = el;
	};

	return (
		<div data-component='TextAreaField' className={cn(className, 'flex flex-col')}>
			{label &&
				<label htmlFor={textAreaId} className='mb-1.5 block w-fit'>
					<FieldLabel required={required} disabled={disabled}>{label}</FieldLabel>
				</label>}
			<textarea
				ref={setRefs}
				id={textAreaId}
				data-component='TextArea'
				data-testid={testId}
				disabled={disabled}
				maxLength={maxLength}
				aria-required={required || undefined}
				aria-invalid={error ? true : undefined}
				aria-describedby={message ? messageId : undefined}
				className={cn(boxStyles, border)}
				{...props}/>
			{(message || maxLength !== undefined) &&
				<Stack alignItems='flexStart' fullWidth>
					{message && <FieldMessage id={messageId} error={Boolean(error)}>{message}</FieldMessage>}
					{maxLength !== undefined &&
						<Typography variant='small3' color='textSecondary' className='ml-auto mt-0.5 shrink-0'>
							{count}/{maxLength}
						</Typography>}
				</Stack>}
		</div>
	);
});

TextArea.displayName = 'TextArea';

const boxStyles =
	'thin-scroll max-h-[150px] min-h-[60px] w-full resize-none overflow-y-auto [scrollbar-gutter:stable] ' +
	'rounded-md border bg-transparent p-2 text-sm text-foreground-contrast outline-none transition-colors ' +
	'placeholder:text-foreground-300 disabled:cursor-not-allowed disabled:text-foreground-300';
